import * as THREE from 'three';
import { QualityLevel } from './EffectsManager';

// Post-processing is currently disabled due to module loading issues
// This will fallback to normal rendering
let EffectComposer: any = null;
let RenderPass: any = null; 
let UnrealBloomPass: any = null;
let SAOPass: any = null;
let ShaderPass: any = null;
let FXAAShader: any = null;

export interface PostProcessingConfig {
  bloom: {
    enabled: boolean;
    strength: number;
    radius: number;
    threshold: number;
  };
  sao: {
    enabled: boolean;
    intensity: number;
    radius: number;
    bias: number;
  };
  colorGrading: {
    enabled: boolean;
    saturation: number;
    brightness: number;
    contrast: number;
  };
  antialiasing: {
    enabled: boolean;
    type: 'FXAA' | 'SMAA';
  };
}

export class PostProcessingPipeline {
  private composer: any;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  
  // Passes
  private renderPass: any;
  private bloomPass?: any;
  private saoPass?: any;
  private fxaaPass?: any;
  private colorGradingPass?: any;
  
  // Configuration
  private config: PostProcessingConfig;
  private quality: QualityLevel = 'medium';
  private enabled = true;
  
  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: Partial<PostProcessingConfig>
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    
    this.config = {
      bloom: {
        enabled: true,
        strength: 0.3,
        radius: 0.4,
        threshold: 0.85
      },
      sao: {
        enabled: false, // Expensive, only for high quality
        intensity: 0.1,
        radius: 32,
        bias: 0.5
      },
      colorGrading: {
        enabled: true,
        saturation: 1.1,
        brightness: 0.05,
        contrast: 1.05
      },
      antialiasing: {
        enabled: true,
        type: 'FXAA'
      },
      ...config
    };
    
    this.initializeComposer();
    this.setupPasses();
  }
  
  private initializeComposer(): void {
    if (!EffectComposer) {
      console.warn('[PostProcessingPipeline] EffectComposer not available, disabling post-processing');
      this.enabled = false;
      return;
    }
    
    this.composer = new EffectComposer(this.renderer);
    
    // Main render pass
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);
  }
  
  private setupPasses(): void {
    if (!this.enabled) return;
    
    this.setupBloomPass();
    this.setupSAOPass();
    this.setupColorGradingPass();
    this.setupAntialiasingPass();
  }
  
  private setupBloomPass(): void {
    if (!this.config.bloom.enabled || !UnrealBloomPass) return;
    
    const resolution = new THREE.Vector2(
      this.renderer.domElement.width,
      this.renderer.domElement.height
    );
    
    this.bloomPass = new UnrealBloomPass(
      resolution,
      this.config.bloom.strength,
      this.config.bloom.radius,
      this.config.bloom.threshold
    );
    
    this.composer.addPass(this.bloomPass);
  }
  
  private setupSAOPass(): void {
    if (!this.config.sao.enabled || !SAOPass) return;
    
    const resolution = new THREE.Vector2(
      this.renderer.domElement.width,
      this.renderer.domElement.height
    );
    
    this.saoPass = new SAOPass(this.scene, this.camera, resolution.x, resolution.y);
    this.saoPass.params.saoIntensity = this.config.sao.intensity;
    this.saoPass.params.saoScale = this.config.sao.radius;
    this.saoPass.params.saoBias = this.config.sao.bias;
    
    this.composer.addPass(this.saoPass);
  }
  
  private setupColorGradingPass(): void {
    if (!this.config.colorGrading.enabled || !ShaderPass) return;
    
    this.colorGradingPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        saturation: { value: this.config.colorGrading.saturation },
        brightness: { value: this.config.colorGrading.brightness },
        contrast: { value: this.config.colorGrading.contrast }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float saturation;
        uniform float brightness;
        uniform float contrast;
        varying vec2 vUv;
        
        void main() {
          vec4 color = texture2D(tDiffuse, vUv);
          
          // Apply brightness
          color.rgb += brightness;
          
          // Apply contrast
          color.rgb = (color.rgb - 0.5) * contrast + 0.5;
          
          // Apply saturation
          float gray = dot(color.rgb, vec3(0.299, 0.587, 0.114));
          color.rgb = mix(vec3(gray), color.rgb, saturation);
          
          gl_FragColor = color;
        }
      `
    });
    
    this.composer.addPass(this.colorGradingPass);
  }
  
  private setupAntialiasingPass(): void {
    if (!this.config.antialiasing.enabled || !ShaderPass || !FXAAShader) return;
    
    if (this.config.antialiasing.type === 'FXAA') {
      this.fxaaPass = new ShaderPass(FXAAShader);
      
      const resolution = new THREE.Vector2(
        this.renderer.domElement.width,
        this.renderer.domElement.height
      );
      
      this.fxaaPass.material.uniforms['resolution'].value.x = 1 / resolution.x;
      this.fxaaPass.material.uniforms['resolution'].value.y = 1 / resolution.y;
      
      this.composer.addPass(this.fxaaPass);
    }
  }
  
  public setQuality(quality: QualityLevel): void {
    this.quality = quality;
    
    // Adjust post-processing based on quality
    switch (quality) {
      case 'low':
        this.setBloomEnabled(false);
        this.setSAOEnabled(false);
        this.setAntialiasingEnabled(false);
        break;
      case 'medium':
        this.setBloomEnabled(true);
        this.setSAOEnabled(false);
        this.setAntialiasingEnabled(true);
        this.updateBloomSettings(0.2, 0.3, 0.9);
        break;
      case 'high':
        this.setBloomEnabled(true);
        this.setSAOEnabled(false);
        this.setAntialiasingEnabled(true);
        this.updateBloomSettings(0.3, 0.4, 0.85);
        break;
      case 'ultra':
        this.setBloomEnabled(true);
        this.setSAOEnabled(true);
        this.setAntialiasingEnabled(true);
        this.updateBloomSettings(0.4, 0.5, 0.8);
        break;
    }
  }
  
  public setBloomEnabled(enabled: boolean): void {
    if (this.bloomPass) {
      this.bloomPass.enabled = enabled;
    }
  }
  
  public setSAOEnabled(enabled: boolean): void {
    if (this.saoPass) {
      this.saoPass.enabled = enabled;
    }
  }
  
  public setAntialiasingEnabled(enabled: boolean): void {
    if (this.fxaaPass) {
      this.fxaaPass.enabled = enabled;
    }
  }
  
  public updateBloomSettings(strength: number, radius: number, threshold: number): void {
    if (this.bloomPass) {
      this.bloomPass.strength = strength;
      this.bloomPass.radius = radius;
      this.bloomPass.threshold = threshold;
    }
  }
  
  public updateColorGrading(saturation: number, brightness: number, contrast: number): void {
    if (this.colorGradingPass) {
      this.colorGradingPass.uniforms.saturation.value = saturation;
      this.colorGradingPass.uniforms.brightness.value = brightness;
      this.colorGradingPass.uniforms.contrast.value = contrast;
    }
  }
  
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  public render(deltaTime?: number): void {
    if (this.enabled && this.composer) {
      this.composer.render(deltaTime);
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }
  
  public setSize(width: number, height: number): void {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
    
    // Update FXAA resolution
    if (this.fxaaPass) {
      this.fxaaPass.material.uniforms['resolution'].value.x = 1 / width;
      this.fxaaPass.material.uniforms['resolution'].value.y = 1 / height;
    }
    
    // Update SAO resolution
    if (this.saoPass) {
      this.saoPass.setSize(width, height);
    }
  }
  
  public getConfig(): PostProcessingConfig {
    return { ...this.config };
  }
  
  public updateConfig(newConfig: Partial<PostProcessingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Rebuild passes if needed
    this.composer.passes = [this.renderPass];
    this.setupPasses();
  }
  
  public getPerformanceImpact(): {
    passCount: number;
    estimatedCost: 'low' | 'medium' | 'high';
    activeEffects: string[];
  } {
    const activePasses = this.composer.passes.filter(pass => pass.enabled);
    const activeEffects: string[] = [];
    
    if (this.bloomPass?.enabled) activeEffects.push('bloom');
    if (this.saoPass?.enabled) activeEffects.push('sao');
    if (this.fxaaPass?.enabled) activeEffects.push('fxaa');
    if (this.colorGradingPass?.enabled) activeEffects.push('colorGrading');
    
    let estimatedCost: 'low' | 'medium' | 'high' = 'low';
    if (activePasses.length > 3 || this.saoPass?.enabled) {
      estimatedCost = 'high';
    } else if (activePasses.length > 1) {
      estimatedCost = 'medium';
    }
    
    return {
      passCount: activePasses.length,
      estimatedCost,
      activeEffects
    };
  }
  
  public dispose(): void {
    if (this.composer) { this.composer.dispose(); }
    
    // Dispose individual passes
    if (this.renderPass) this.renderPass.dispose();
    if (this.bloomPass) this.bloomPass.dispose();
    if (this.saoPass) this.saoPass.dispose();
    if (this.fxaaPass) this.fxaaPass.dispose();
    if (this.colorGradingPass) this.colorGradingPass.dispose();
  }
}